import React, { useContext, useEffect, useRef, useState } from "react";
import { Basic } from "react-dial-knob";
import GratefulDB, { ChunkConfig, Entry } from "../../GratefulDB";
import GratefulDBContext from "../../GratefulDBContext";
import JerryBear from "../../images/JerryBearTransparent.png";
import { entryVenueCoverage } from "../shared/Helpers";
import LCDDisplay from "../shared/LCDDisplay";
import { AudioControls } from "../shared/useAudioControls";
import "./TimeMachine.scss";

export const TimeMachine: React.FC<{
  audioControls: AudioControls;
  chunkConfig: ChunkConfig;
}> = (props): JSX.Element => {
  const gratefulDB = useContext<GratefulDB>(GratefulDBContext);
  const setlist = useRef<HTMLDivElement>(null);
  const activeTrack = useRef<HTMLDivElement>(null);

  const [interactionState, setInteractionState] = useState<{
    entry?: Entry;
  } | null>(null);

  const [y, m, d] = props.chunkConfig.dateEnd.split("-").map(Number);
  const [year, setYear] = useState<number>(y);
  const [month, setMonth] = useState<number>(m);
  const [day, setDay] = useState<number>(d);

  const knobColor = process.env.REACT_APP_PRIMARY_COLOR;
  const knobStyle = { marginLeft: "auto", marginRight: "auto" };
  const knobTheme = {
    defaultColor: knobColor,
    activeColor: knobColor,
    notchAndText: knobColor,
  };

  const getMetadata = () => {
    if (interactionState && interactionState.entry) {
      return {
        title: "---",
        date: interactionState.entry.date,
        venueCoverage: entryVenueCoverage(interactionState.entry),
      };
    }
    const entry = props.audioControls.getEntry();
    const track = props.audioControls.getTrack();
    if (!interactionState && entry && track) {
      return {
        title: track.title,
        date: entry.date,
        venueCoverage: entryVenueCoverage(entry),
      };
    }
    return {
      title: "---",
      date: "---",
      venueCoverage: "---",
    };
  };

  const random = () => {
    const entry = gratefulDB.randomTimeMachineEntry();
    if (!entry) {
      return;
    }
    props.audioControls.selectTrack(entry, entry.tracks[0], false);
  };

  const interactionChange = (isInteracting: boolean) => {
    if (!isInteracting && interactionState && interactionState.entry) {
      props.audioControls.selectTrack(
        interactionState.entry,
        interactionState.entry.tracks[0],
        false
      );
    }
    setInteractionState(isInteracting ? {} : null);
  };

  useEffect(() => {
    if (!interactionState) {
      return;
    }
    setInteractionState({
      entry: gratefulDB.findClosestShow(year, month, day),
    });
  }, [year, month, day]);

  const setlistScrollTop = () => {
    if (setlist.current) {
      setlist.current.scrollTop = 0;
    }
  };

  const setlistScrollActive = () => {
    if (!activeTrack.current || !setlist.current) {
      return;
    }
    const parent = setlist.current.getBoundingClientRect();
    const active = activeTrack.current.getBoundingClientRect();
    const offsetActive = active.top - parent.top + active.height;
    const isVisible = offsetActive <= parent.height;
    if (isVisible) {
      return;
    }
    activeTrack.current.scrollIntoView();
  };

  useEffect(() => {
    const entry = props.audioControls.getEntry();
    if (!props.audioControls.isVisible() || !entry) {
      return;
    }

    const splitDate = entry.date.split("-");
    if (splitDate.length !== 3) {
      return;
    }
    setYear(parseInt(splitDate[0]));
    setMonth(parseInt(splitDate[1]));
    setDay(parseInt(splitDate[2]));

    const firstActive = props.audioControls.isActiveTrack(
      entry,
      entry.tracks[0]
    );
    if (firstActive) {
      setlistScrollTop();
    } else {
      setlistScrollActive();
    }
  }, [props.audioControls.getPlayerState()]);

  const metadata = getMetadata();
  const getTracks = () => {
    const entry = props.audioControls.getEntry();
    return !entry || !props.audioControls.isVisible() ? [] : entry.tracks;
  };

  const renderSetlist = () => {
    const entry = props.audioControls.getEntry();
    if (!entry) {
      return;
    }
    return (
      <div>
        <div
          className="ui relaxed items link scrollable divided inlay setlist"
          ref={setlist}
          style={{ backgroundImage: `url(${JerryBear})` }}
        >
          {getTracks().map((track) => (
            <div
              className="item"
              onClick={() =>
                props.audioControls.selectTrack(entry, track, false)
              }
              ref={
                props.audioControls.isActiveTrack(entry, track)
                  ? activeTrack
                  : null
              }
              key={track.name}
            >
              <div className="ui middle aligned content">
                <h3 className="ui header">
                  {props.audioControls.isActiveTrack(entry, track) ? (
                    <i className="volume up small icon active-track" />
                  ) : null}
                  {track.title}
                </h3>
                <h4 className="ui header right floated track-length">
                  {track.length}
                </h4>
              </div>
            </div>
          ))}
        </div>
        <div
          className={`ui teal ${
            props.audioControls.isLoading() ? "loading" : ""
          } large random-button button`}
          onClick={random}
        >
          RANDOM SHOW
        </div>
      </div>
    );
  };

  return (
    <div className="TimeMachine">
      <div className="ui container">
        <div className="ui grid">
          <div className="sixteen wide tablet eleven wide computer tablet computer only column">
            <LCDDisplay
              title={metadata.title}
              date={metadata.date}
              venueCoverage={metadata.venueCoverage}
              marquee={interactionState === null || !interactionState.entry}
            />
            <div className="ui center aligned grid inlay knobs">
              <div className="five wide center aligned column">
                <Basic
                  diameter={150}
                  min={props.chunkConfig.yearStart}
                  max={props.chunkConfig.yearEnd}
                  step={1}
                  value={year}
                  theme={knobTheme}
                  knobStyle={knobStyle}
                  onValueChange={setYear}
                  onInteractionChange={interactionChange}
                />
                <h3>YEAR</h3>
              </div>
              <div className="five wide center aligned  column">
                <Basic
                  diameter={150}
                  min={1}
                  max={12}
                  step={1}
                  value={month}
                  theme={knobTheme}
                  knobStyle={knobStyle}
                  onValueChange={setMonth}
                  onInteractionChange={interactionChange}
                />
                <h3>MONTH</h3>
              </div>
              <div className="five wide center aligned column">
                <Basic
                  diameter={150}
                  min={1}
                  max={31}
                  step={1}
                  value={day}
                  theme={knobTheme}
                  knobStyle={knobStyle}
                  onValueChange={setDay}
                  onInteractionChange={interactionChange}
                />
                <h3>DAY</h3>
              </div>
            </div>
          </div>
          {props.audioControls.isVisible() ? (
            <div className="five wide computer only middle aligned column">
              {renderSetlist()}
            </div>
          ) : (
            <div className="five wide computer only bottom aligned center aligned column spin-prompt">
              <i className="ui reply link icon" onClick={random} />
              <h3 className="ui header">
                Spin one of the dials to explore the catalog.
              </h3>
            </div>
          )}
          <div className="mobile only sixteen wide center aligned column mobile-disclaimer">
            <i className="ui clock icon" />
            <h2 className="ui header">
              Sorry, the time machine is not available on mobile devices.
            </h2>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeMachine;
